<template>
  <div :key="`stats-${reportingStore.isSidebarShown}`" class="h-100 d-flex flex-column">
    <ReportingSection :short-view="shortView">
      <template #header>
        <ReportingHeader :short-view="shortView" :section="section">
          <template #filter>
            <AdvancedFilters
              v-if="!shortView"
              :filters="reportingStore.filters[section]"
              :filter-options="reportingStore.filterOptions"
              :btn-text="filterButtonText"
              :disabled="!canViewStatsReport || filtersDisabled"
              class="ml-3"
              @change="handleFilterChange"
              @apply="applyFilters"
            />
          </template>
        </ReportingHeader>
      </template>
      <template #main-section>
        <ReportingMainSection
          :section="section"
          :short-view="shortView"
          :subtitle="tkey('subtitle')"
        >
          <template #data-section>
            <div class="d-flex w-100 mr-5 h-100">
              <div v-if="!canViewStatsReport" class="ma-auto no-data">
                {{ t('reportingPage.noPermissions') }}
              </div>
              <BaseProgressBar
                v-else-if="isTableLoading"
                :message="t('reportingPage.loadingTable')"
                style="margin: auto"
                class="pt-5"
              />
              <AgGridVue
                v-else
                tree-data
                animate-rows
                dense
                class="w-100"
                :column-defs="headers"
                :row-data="tableData"
                :grid-options="gridOptions"
                @grid-ready="onGridReady"
              />
            </div>
            <div v-if="!shortView" class="w-100 mr-5 h-100" style="height: initial">
              <BaseProgressBar
                v-if="isChartLoading"
                :message="t('reportingPage.loadingChart')"
                :style="{ margin: 'auto' }"
                class="pt-5"
              />
              <div v-else class="chart-area">
                <div class="header d-flex">
                  <span class="header-text">{{ tkey('absoluteChange') }}</span>
                  <v-select
                    v-model="reportingStore.metricValue"
                    :items="statsMetrics"
                    :disabled="!canViewStatsReport"
                    item-title="text"
                    item-value="type"
                    :menu-props="{
                      contentClass: 'options',
                      location: 'bottom center',
                    }"
                    :placeholder="t('general.select')"
                    hide-details
                    single-line
                    class="ml-5 mr-3 filter-select rtls-select select"
                    bg-color="white"
                    :menu-icon="null"
                    @update:model-value="value => onSelectionChanged('metricValue', value)"
                  >
                    <template #append-inner>
                      <v-icon role="img" aria-hidden="false" icon="mdi-chevron-down" size="large" />
                    </template>
                    <template #item="data">
                      <v-list-item v-bind="data.props" class="slot-item" />
                    </template>
                  </v-select>

                  <v-select
                    v-model="reportingStore.attributeValue"
                    :items="
                      reportingStore.getAttributesIntersection(
                        reportingStore.baselineIds,
                        reportingStore.comparisonIds
                      )
                    "
                    :disabled="!canViewStatsReport"
                    item-title="name"
                    item-value="id"
                    :menu-props="{
                      contentClass: 'options',
                      location: 'bottom center',
                    }"
                    :placeholder="t('general.select')"
                    hide-details
                    single-line
                    class="mr-3 filter-select rtls-select select"
                    bg-color="white"
                    :menu-icon="null"
                    @update:model-value="value => onSelectionChanged('attributeValue', value)"
                  >
                    <template #append-inner>
                      <v-icon
                        role="img"
                        aria-hidden="false"
                        size="10"
                        icon="rtls:BaseIconExpandMore"
                      />
                    </template>
                    <template #item="data">
                      <v-list-item v-bind="data.props" class="slot-item" />
                    </template>
                  </v-select>
                </div>
                <highcharts
                  v-if="canViewStatsReport && hasAvailableChartData"
                  :key="highchartKey"
                  :options="chartOptions"
                />
              </div>
            </div>
          </template>
        </ReportingMainSection>
      </template>
      <template #footer>
        <ReportingFooter
          :short-view="shortView"
          :is-export-disabled="!tableData.length"
          @export="exportCSV"
        />
      </template>
    </ReportingSection>
  </div>
</template>

<script>
import {
  forEach,
  get,
  groupBy,
  includes,
  intersection,
  keys,
  lowerCase,
  map,
  reduce,
  some,
  size,
  sortBy,
  sum,
  union,
} from 'lodash';

// plugins and utils
import defaultChartOptions from '@/plugins/default-chart-options';
import owColours from '@/plugins/ow-colors';
import agGridUtils from '@/utils/ag-grid-utils';

// enums
import reportingLevel from '@enums/reporting-data-level';
import reportingFilters from '@enums/reporting-filters';
import reportingMetrics from '@enums/reporting-metrics';
import reportingSections from '@enums/reporting-sections';

// composables
import { useExportCsv } from '@/composables/use-export-csv';
import { useInternationalization } from '@/composables/use-internationalization';
import { usePermission } from '@/composables/use-permission';
import { useFormatNumber } from '@/composables/use-format-number';

// stores
import { useContextStore } from '@/stores/context';
import { useReportingStore } from '@/stores/reporting';
import { useWorkpackageScenarioSelectStore } from '@/stores/workpackage-scenario-select';

export default {
  beforeRouteLeave(to) {
    // Shrink is only a valid metric for the Stats tab, so when navigating away, default
    // back to Sales as the selected metric and update the URL querystring.
    if (this.reportingStore.metricValue === reportingMetrics.metrics.shrink) {
      this.reportingStore.metricValue = reportingMetrics.metrics.sales;
      return { ...to, query: { ...to.query, metricValue: reportingMetrics.metrics.sales } };
    }
  },
  props: {
    // represents whether this report is open on (short) overview page
    shortView: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  setup() {
    const { t, tkey } = useInternationalization('reportingPage.sections.stats');
    const { getFileName } = useExportCsv();
    const { canViewStatsReport } = usePermission();
    const { formatNumber } = useFormatNumber();

    const reportingStore = useReportingStore();
    const contextStore = useContextStore();
    const workpackageScenarioSelectStore = useWorkpackageScenarioSelectStore();

    return {
      // i18n
      t,
      tkey,
      // export csv
      getFileName,
      // permissions
      canViewStatsReport,
      // formatters
      formatNumber,
      // stores
      reportingStore,
      contextStore,
      workpackageScenarioSelectStore,
    };
  },

  data() {
    return {
      reportingSections,
      tableData: [],
      chartData: [],
      gridApi: null,
      gridOptions: {
        columnMenu: 'legacy',
        rowHeight: 30,
        headerHeight: 40,
        suppressContextMenu: true,
        rowClassRules: {
          'baseline-row': ({ data }) => data.checkpointType === reportingFilters.baseline,
          'comparison-row': ({ data }) => data.checkpointType === reportingFilters.comparison,
          'comparison2-row': ({ data }) => data.checkpointType === reportingFilters.comparison2,
        },
        defaultColDef: {
          editable: false,
          sortable: false,
          filter: false,
          menuTabs: [],
          suppressMovable: true, // do not make headers movable
          resizable: true,
          suppressHeaderMenuButton: true, // hide header menu by default
        },
        autoGroupColumnDef: {
          headerName: '',
          field: 'name',
          resizable: true,
          minWidth: 150,
          cellClassRules: {
            'font-weight-bold': ({ data }) =>
              data && includes([reportingLevel.bundle, reportingLevel.workpackage], data.level),
          },
          cellRendererParams: { suppressCount: true },
          tooltipShowDelay: 0,
          tooltipValueGetter: params => get(params, 'data.name', ''),
        },
        getDataPath(data) {
          return data.hierarchy;
        },
        getRowId(row) {
          const { data } = row;
          return `${data._id}-${data.checkpointType}`;
        },
      },
      section: reportingSections.stats,
      chartLoading: false,
    };
  },

  computed: {
    headers() {
      const headers = [
        {
          headerName: this.tkey('table.products'),
          field: 'totalProducts',
          headerClass: 'bold',
          aggFunc: params => this.getAggValue(params),
        },
        {
          headerName: this.tkey('table.stores'),
          field: 'totalStores',
          headerClass: 'bold',
          aggFunc: params => this.getAggValue(params),
        },
        {
          headerName: this.tkey('table.sales'),
          field: 'totalSales',
          headerClass: 'bold',
          aggFunc: params => this.getAggValue(params),
          valueFormatter: params => {
            return this.formatNumber({ number: params.value, format: 'currency' });
          },
        },
        {
          headerName: this.tkey('table.volume'),
          field: 'totalVolume',
          headerClass: 'bold',
          aggFunc: params => this.getAggValue(params),
          valueFormatter: params => {
            return this.formatNumber({ number: params.value, format: 'float' });
          },
        },
        {
          headerName: this.tkey('table.margin'),
          field: 'totalMargin',
          headerClass: 'bold',
          aggFunc: params => this.getAggValue(params),
          valueFormatter: params => {
            return this.formatNumber({ number: params.value, format: 'currency' });
          },
        },
      ];

      // Additional column headers can be retrieved from config
      const extraColumns = get(this.contextStore.getClientConfig, 'statsReportColumns', []);
      forEach(extraColumns, c => {
        const addOptionalColumnDef = this.optionalColumnHeaders[c];
        if (addOptionalColumnDef) headers.push(addOptionalColumnDef);
      });

      return headers;
    },

    optionalColumnHeaders() {
      return {
        marginPercent: {
          headerName: this.tkey('table.marginPercent'),
          headerClass: 'bold',
          valueGetter: this.getMarginPercent,
          valueFormatter: params => (params.value ? `${(params.value * 100).toFixed(2)}%` : '-'),
        },
        ...(this.anyWorkpackagesAreFresh && {
          shrink: {
            headerName: this.tkey('table.shrink'),
            field: 'totalShrink',
            headerClass: 'bold',
            aggFunc: params => this.getAggValue(params),
            valueFormatter: params => {
              return this.formatNumber({ number: params.value, format: 'currency' });
            },
          },
          shrinkPercent: {
            headerName: this.tkey('table.shrinkPercent'),
            headerClass: 'bold',
            valueGetter: this.getShrinkPercent,
            valueFormatter: params => (params.value ? `${(params.value * 100).toFixed(2)}%` : '-'),
          },
        }),
      };
    },

    anyWorkpackagesAreFresh() {
      if (this.workpackageScenarioSelectStore.selectedWorkpackages) {
        return some(
          this.workpackageScenarioSelectStore.selectedWorkpackages,
          wp => wp.type === 'a_fresh'
        );
      }

      return false;
    },

    statsMetrics() {
      const metrics = this.reportingStore.enabledMetrics;
      if (!this.anyWorkpackagesAreFresh) return metrics;
      // Only include Shrink as an option for Fresh workpackages, inserted after Margin.
      const shrinkMetric = {
        type: reportingMetrics.metrics.shrink,
        text: this.t('reportingPage.metrics.shrink'),
      };
      // Place shrink after margin in the dropdown.
      const marginIndex = metrics.findIndex(m => m.type === reportingMetrics.metrics.margin);
      if (marginIndex === -1) return [...metrics, shrinkMetric];
      return [
        ...metrics.slice(0, marginIndex + 1),
        shrinkMetric,
        ...metrics.slice(marginIndex + 1),
      ];
    },

    hasValidChartSelection() {
      return !!this.reportingStore.attributeValue && !!this.reportingStore.metricValue;
    },

    isTableLoading() {
      return this.reportingStore.loadingSubsection.statsTable;
    },

    isChartLoading() {
      return this.reportingStore.loadingSubsection.statsChart || this.chartLoading;
    },

    hasAvailableChartData() {
      return this.hasValidChartSelection && !this.isChartLoading && size(this.chartData);
    },

    filtersDisabled() {
      return !this.hasAvailableChartData && this.isTableLoading;
    },

    getAllAttributesValues() {
      const currentAttributeSelected = this.reportingStore.getAttributeFromId;
      if (!currentAttributeSelected || !size(this.chartData)) return [];

      return this.attributeValuesByNameMap[lowerCase(currentAttributeSelected.name)] || [];
    },

    // Combine the baseline and comparison attribute values so that we have full lists for filters
    attributeValuesByNameMap() {
      return this.chartData.reduce((acc, checkpoint) => {
        forEach(checkpoint.attributesValues, (values, key) => {
          acc[key] = union(acc[key], values);
          return acc;
        });
        return acc;
      }, {});
    },

    filterButtonText() {
      const filtersCount = size(this.reportingStore.filters[this.currentSection]);
      return `${this.t('reportingPage.filters', filtersCount)}`;
    },

    getSeries() {
      if (!size(this.chartData)) return [];
      const checkpointsByType = groupBy(this.chartData, 'checkpointType');
      const selectedComparisons = intersection(keys(checkpointsByType), [
        reportingFilters.comparison,
        reportingFilters.comparison2,
      ]);

      return map(selectedComparisons, c => {
        const attributeData = this.getAllAttributesValues.map(attributeValue => {
          const totalInBaseline = this.sumAttributeTotal(
            checkpointsByType[reportingFilters.baseline],
            attributeValue
          );
          const totalInComparison = this.sumAttributeTotal(checkpointsByType[c], attributeValue);

          return {
            y: totalInComparison - totalInBaseline,
            baseline: totalInBaseline,
            comparison: totalInComparison,
            type: c,
          };
        });

        return {
          name: this.t(`reportingPage.menuHeaders.${c}`, 2),
          data: attributeData,
        };
      });
    },

    cacheKey() {
      // Create a composite key for report cache from section, metric, attribute and filters
      const filtersKey = this.reportingStore.filters[this.currentSection]
        .map(f => {
          const operator = f.notEqualTo ? 'neq' : 'eq';
          const filterValuesStr = f.filterValues
            .map(v => v.name)
            .sort()
            .join(',');
          return `${f.filterName}:${operator}:${filterValuesStr}`;
        })
        .sort()
        .join('|');
      return `${this.currentSection}_${this.reportingStore.metricValue}_${this.reportingStore.attributeValue}_${filtersKey}`;
    },

    chartOptions() {
      const tkey = key => this.t(`reportingPage.${key}`);
      const fn = number =>
        this.formatNumber({
          number,
          format: reportingMetrics.chartsLookup[this.reportingStore.metricValue].numberFormat,
        });

      return {
        colors: [
          owColours.statsReport.columnChartSeriesColor1,
          owColours.statsReport.columnChartSeriesColor2,
        ],
        chart: {
          ...defaultChartOptions.chart,
          type: 'column',
        },
        title: { text: null },
        yAxis: {
          allowDecimals: false,
          title: { text: this.tkey('chart.yTitle') },
        },
        xAxis: {
          categories: [...this.getAllAttributesValues],
          labels: { style: { color: owColours.statsReport.chartFontColor } },
        },
        credits: { enabled: false },
        tooltip: {
          formatter() {
            return `${tkey('baseline')}: ${fn(this.point.baseline)}<br/>${tkey(
              this.point.type
            )}: ${fn(this.point.comparison)}<br/>${tkey('delta')}: <b>${fn(this.point.y)}</b>`;
          },
        },
        series: this.getSeries,
        plotOptions: {
          series: {
            dataLabels: {
              // dont show total per attribute value
              enabled: false,
              formatter() {
                return fn(this.y);
              },
            },
          },
        },
      };
    },

    highchartKey() {
      const valuesKeys = this.getAllAttributesValues.reduce((acc, value) => `${acc}-${value}`, '');
      return `${this.reportingStore.metricValue}-${this.reportingStore.attributeValue}-${valuesKeys}`;
    },

    currentSection() {
      return reportingSections.stats;
    },
  },

  watch: {
    'reportingStore.reportsGenerated': function () {
      this.fetchData();
    },
  },

  created() {
    this.fetchData();
  },

  methods: {
    onGridReady(params) {
      this.gridApi = params.api;
      params.api.sizeColumnsToFit();
    },

    parseTableData(rawData) {
      return sortBy(rawData, ['checkpointType', 'spacebreakSize']).map(r => {
        // add canvas name when data isn't a checkpoint
        if (!r.name) r.name = this.reportingStore.getCanvasName(r);

        return r;
      });
    },

    handleFilterChange(filters) {
      this.reportingStore.setReportingFilters({ section: this.currentSection, filters });
    },

    applyFilters() {
      this.fetchData();
    },

    async onSelectionChanged(field, value) {
      let fetchData = false;
      // If previously we had an invalid selection then we should fetch data
      if (!this.hasValidChartSelection) {
        fetchData = true;
      }
      await this.reportingStore.setSelection({ field, value });

      this.navigateToNewUrl({ fetchData });
    },

    // include new query to url param
    navigateToNewUrl({ fetchData }) {
      const newQueryValues = {
        ...this.$route.query,
        metricValue: this.reportingStore.metricValue,
        attributeValue: this.reportingStore.attributeValue,
      };

      this.$router.push({
        path: this.$route.path, // keep on same path
        query: newQueryValues,
      });
      if (fetchData) this.fetchData();
    },

    getMarginPercent(params) {
      const data = params.node.aggData ? params.node.aggData : params.data;
      const { totalMargin, totalSales } = data;
      return totalSales ? totalMargin / totalSales : 0;
    },

    getShrinkPercent(params) {
      const data = params.node.aggData ? params.node.aggData : params.data;
      const { totalShrink, totalSales } = data;
      return totalSales ? totalShrink / totalSales : 0;
    },

    getAggValue(params) {
      if (params.colDef.field === 'totalProducts') return get(params, 'data.totalProducts', 0);
      if (params.colDef.field === 'totalStores') return get(params, 'data.totalStores', 0);

      return sum(get(params, 'values', []));
    },

    sumAttributeTotal(checkpoints, attributeValue) {
      return reduce(
        checkpoints,
        (acc, checkpoint) => {
          const checkpointMetricForValue = get(
            checkpoint.metricsData,
            `${lowerCase(this.reportingStore.getAttributeFromId.name)}.${lowerCase(attributeValue)}`
          );
          // although some attribute values may be present on a scenario, not every product is assigned it spacebreaks,
          // so we assume 0 for their totals
          return checkpointMetricForValue
            ? acc +
                checkpointMetricForValue[
                  reportingMetrics.chartsLookup[this.reportingStore.metricValue].field
                ]
            : acc;
        },
        0
      );
    },

    getAllExportableDataColumns() {
      return this.gridApi.getAllDisplayedColumns();
    },

    processCellCallback(params) {
      // Being called for each cell

      if (includes(['totalSales', 'totalMargin', 'totalShrink'], params.column.colId)) {
        return this.formatNumber({ number: params.value, format: 'currency' });
      }

      if (params.column.colId === 'totalVolume') {
        return this.formatNumber({ number: params.value, format: 'float' });
      }

      return agGridUtils.utils.processCellForExport(params);
    },

    exportCSV() {
      const exportParams = {
        fileName: this.getFileName({
          serviceName: 'stats-report',
          fileNameDateFormat: this.contextStore.getDateFormats.csvFileName,
        }),
        suppressQuotes: this.contextStore.getCsvExport.suppressQuotes,
        columnSeparator: this.contextStore.getCsvExport.columnSeparator,
        columnKeys: this.getAllExportableDataColumns(),
        processCellCallback: this.processCellCallback,
      };

      this.gridApi.exportDataAsCsv(exportParams);
    },

    async fetchData() {
      this.chartLoading = true;
      const cachedData = get(this.reportingStore.reportData, this.cacheKey);

      // Check if we have cached data for table and chart
      const existingTableData = get(cachedData, 'table');
      const existingChartData = get(cachedData, 'chart');

      const params = {
        section: this.currentSection,
        productFilters: this.reportingStore.filters[this.currentSection],
      };

      // Fetch table data if not cached
      const tablePromise = !existingTableData
        ? this.reportingStore.fetchReportingData({ ...params, reportArea: 'table' })
        : Promise.resolve(existingTableData);

      // Fetch chart data if not cached and we have valid selection
      const shouldFetchChart = !this.shortView && this.hasValidChartSelection;
      const chartPromise =
        shouldFetchChart && !existingChartData
          ? this.reportingStore.fetchReportingData({ ...params, reportArea: 'chart' })
          : Promise.resolve(existingChartData);

      const [tableData, chartData] = await Promise.all([tablePromise, chartPromise]);

      // Store fetched data in report store cache
      this.reportingStore.updateReportData(`${this.cacheKey}.table`, tableData);
      if (shouldFetchChart) {
        this.reportingStore.updateReportData(`${this.cacheKey}.chart`, chartData);
      }

      this.tableData = this.parseTableData(tableData);

      if (chartData) this.chartData = chartData;
      this.chartLoading = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.chart-area {
  .header {
    justify-content: space-between;

    .header-text {
      align-self: center;
    }
  }
}

:deep(.baseline-row) {
  background-color: lighten($baseline-colour, 60%) !important;
}

:deep(.comparison-row) {
  background-color: lighten($comparison-colour, 30%) !important;
}

:deep(.comparison2-row) {
  background-color: lighten($comparison2-colour, 30%) !important;
}

:deep(.ag-theme-alpine .ag-cell-wrapper) {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  padding-left: 0 !important;
}

:deep(.ag-theme-alpine .ag-row-group-indent-1) {
  padding-left: 15px !important;
}

:deep(.ag-theme-alpine .ag-row-group-indent-2) {
  padding-left: 25px !important;
}

:deep(.ag-theme-alpine .ag-row-group-indent-3) {
  padding-left: 35px !important;
}

:deep(.ag-theme-alpine .ag-row-group-indent-4) {
  padding-left: 45px !important;
}

.no-data {
  font-size: 1.4rem;
}

.filter-select {
  :deep(.v-icon) {
    opacity: unset;
  }
  :deep(.v-input__control) {
    height: 32px;
  }
  :deep(.v-field__field) {
    height: 32px;
  }
  :deep(.v-field__input) {
    min-height: 32px;
    padding-top: 8px;
  }
  :deep(.v-list) {
    padding: 0 !important;
  }

  :deep(v-field--focused) {
    background-color: red !important;
  }

  :deep(
      .v-field--focused.v-field--variant-filled .v-field__outline::after,
      .v-field--focused.v-field--variant-underlined .v-field__outline::after
    ) {
    border-color: $assortment-primary-colour !important;
  }
}

.rtls-select {
  .v-input__slot,
  .v-select__slot {
    background: white;
    height: 32px !important;
  }
  &.select {
    border-radius: 0px;
    height: 32px;
    .v-input__control {
      min-height: 32px;
    }
  }
}

.slot-item {
  min-height: 32px !important;
}
</style>
